import React from "react";
import type { BraillerLoanManagementSearchCriteria, BraillerLoanFilterLists } from '../brailler.types';
import {
    Button,
    convertInputFormToObject, DateTimePickerField,
    InputForm,
    inputStyles,
    TextField,
    useInputForm
} from "../../../../Components/Inputs";
import {clearInputFormFields} from "../../../../Components/Inputs/inputUtility";
import {GridCell, gridConstants, GridContainer} from "../../../../Components/Grid";
import {SearchableSingleSelectField} from "../../../../Components/Inputs/SearchableSingleSelectField";
import {isAdmin} from "../../../../Utilities/LocalStorage/storageUtilities";
import {createListFromArray, createListFromArrayOfObjects} from "../../../../Utilities/Types/listUtilities";

export const BraillerLoanManagementSearchFormView = ({
                                                         searchCriteria,
                                                         searchDropDowns,
                                                         handleSetSearchCriteria,
                                                         isLoading,
                                                     }: {
    searchCriteria: BraillerLoanManagementSearchCriteria;
    searchDropDowns: BraillerLoanFilterLists;
    handleSetSearchCriteria: (criteria: Partial<BraillerLoanManagementSearchCriteria>) => void;
    isLoading: boolean;
}) => {

    const searchForm = useInputForm(searchCriteria);

    const districts = createListFromArrayOfObjects(searchDropDowns?.districts, "districtId", "districtName");
    const organizations = createListFromArray(searchDropDowns?.organizations);

    // Enum values (Loans=1, Repairs=2, Both=3) - show placeholder text
    const viewTypeOptions = [
        {value: 3, text: 'Select Option'},
        {value: 1, text: 'Loans'},
        {value: 2, text: 'Repairs'}
    ];

    const handleClearClick = () => {
        clearInputFormFields(searchForm);
        handleSetSearchCriteria({
            viewType: 3,
            startDate: null,
            endDate: null,
            searchKeyword: '',
            page: 1,
            recordsPerPage: 25,
            sortByColumn: 'dateCheckedOut',
            sortDescending: true,
            loadSearchResults: true,
        });
    };

    const handleSearchClick = () => {
        let criteria = convertInputFormToObject(searchForm);
        criteria.viewType = parseInt(String(criteria.viewType ?? 3));
        handleSetSearchCriteria({
            ...searchCriteria,
            ...criteria,
            page: 1,
            loadSearchResults: true,
        });
    };

    const inputSharedSettings = {
        medium_columns: gridConstants.column.FOUR,
    };

    return <>
        <InputForm name="braillerLoanManagementSearch" hasPaddingY>

            <SearchableSingleSelectField
                {...searchForm.viewType}
                {...inputSharedSettings}
                label="Loans / Repairs"
                options={viewTypeOptions}
            />

            <TextField
                {...searchForm.searchKeyword}
                {...inputSharedSettings}
                label="Search (Title, Serial Number, Issue)"
            />

            <DateTimePickerField
                {...searchForm.startDate}
                {...inputSharedSettings}
                label={'Start Date'}
                name={'Start Date'}
            />

            <DateTimePickerField
                {...searchForm.endDate}
                {...inputSharedSettings}
                label="End Date"
                type="date"
            />

            {
                isAdmin() &&
                <SearchableSingleSelectField
                    {...searchForm.districtId}
                    {...inputSharedSettings}
                    defaultOption={"All"}
                    includeDefaultOption
                    label={'District'}
                    options={districts}
                />
            }
            {
                isAdmin() &&
                <SearchableSingleSelectField
                    {...searchForm.organization}
                    {...inputSharedSettings}
                    includeDefaultOption
                    label={'Organization'}
                    options={organizations}
                />
            }

            <GridCell>
                <GridContainer hasPaddingX isFlush isReverseOrder>

                    <GridCell medium_columns={gridConstants.column.FOUR}>
                        <Button
                            buttonType={inputStyles.button.types.SEARCH}
                            disabled={isLoading}
                            label="Search"
                            name="btnSearchBraillerLoan"
                            onClick={handleSearchClick}
                            isPrimary
                        />
                    </GridCell>

                    <GridCell medium_columns={gridConstants.column.FOUR}>
                        <Button
                            buttonType={inputStyles.button.types.CLEAR}
                            disabled={isLoading}
                            name="btnClearBraillerLoanSearch"
                            onClick={handleClearClick}
                            label="Clear"
                        />
                    </GridCell>

                </GridContainer>
            </GridCell>

        </InputForm>
    </>;
};

